<template>
  <div
    v-tooltip="disabledTooltip"
    class="element-selector"
    :class="{ 'section-selector': section }"
  >
    <button
      v-if="selectMode"
      :disabled="!selectable"
      :data-external="dataExternal"
      class="element-selector-overlay select-button"
      @click="$emit('select')"
    >
      <small class="selection-name">{{ name }}</small>
    </button>
    <div v-else :data-external="dataExternal" class="element-selector-overlay highlight">
      <small class="selection-name">{{ name }}</small>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useCurrentElement } from '@vueuse/core';
import { t } from '../../helpers/translation.helper';
import { useConditionalLogicStore } from '../store/conditional-logic.store';

const props = defineProps<{
  section?: boolean;
  dataExternal: string;
  selectable: boolean;
  disabledTooltip: string;
}>();

defineEmits<{
  select: [];
}>();

const { selectMode } = storeToRefs(useConditionalLogicStore());
const el = useCurrentElement<HTMLElement>();

const name = computed(() => (props.section ? t('block') : t('element')));

const disabledTooltip = computed(() =>
  selectMode.value && !props.selectable ? props.disabledTooltip : ''
);

onMounted(() => {
  if (!selectMode.value) {
    el.value.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
});
</script>

<style scoped>
.element-selector {
  --small-height: 30px;

  position: absolute;
  top: 0;
  bottom: 0;
  width: calc(100% - var(--spacing-200));
  left: var(--spacing-100);

  & .element-selector-overlay {
    background-color: transparent;
    transition: background-color 0.1s ease-in-out;
    border: solid 1px transparent;
    border-radius: var(--radius-medium);
    border-top-left-radius: 0;
    z-index: 4;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    position: absolute;

    & .selection-name {
      position: absolute;
      padding: var(--spacing-50) var(--spacing-100);
      background-color: var(--blue-86);
      color: var(--text-grey);
      height: var(--small-height);
      top: calc(-1 * var(--small-height));
      left: -1px;
      border-radius: var(--radius-medium) var(--radius-medium) 0 0;
      display: none;
      text-transform: capitalize;
    }

    &.highlight {
      background: rgb(from var(--blue-86) r g b / 30%);
      border-color: var(--blue-86);

      & small {
        display: block;
      }
    }

    &.select-button {
      cursor: pointer;

      &:hover,
      &:focus {
        outline: none;
        background: rgb(from var(--blue-86) r g b / 30%);
        border-color: var(--blue-86);

        & small {
          display: block;
        }
      }

      &:disabled:hover {
        cursor: not-allowed;
        background: rgb(from var(--gray-80) r g b / 30%);
        border-color: rgb(from var(--gray-80) r g b / 30%);

        & .selection-name {
          background: var(--gray-80);
        }
      }
    }
  }

  &.section-selector {
    left: 0;
    width: 100%;
    z-index: 3;
    top: -40px;
    bottom: -40px;
  }
}
</style>
